import {
  analyticsBindingMapping,
  AnalyticsContext,
  AnalyticsEventParam,
  AnalyticsScreenViewEvent,
  createAnalyticsPayload,
  ScreenType,
} from '@accedo/one-plugin-sdk';
import { applyDatabinding } from '@dynamic/ui';
import { dset as set } from 'dset';

import { Item } from '#/client';
import {
  FeatureAnalyticsProps,
  FeatureArrayData,
  IntegrationsFeatures,
  MainConfig,
} from '#/service/config';
import { Dictionary } from '#/service/utils';

const { Browse, Details, Search, WatchHistory } = ScreenType;

const screenTypeMapping: Dictionary<string, ScreenType> = {
  details: Details,
  search: Search,
  history: WatchHistory,
};

interface HandleScreenViewParams {
  screenTitle?: string;
  screenType?: ScreenType;
  item?: Item;
}

export const handleScreenView = ({
  screenTitle,
  screenType,
  item,
}: HandleScreenViewParams) => {
  document.dispatchEvent(
    new AnalyticsScreenViewEvent({
      [AnalyticsEventParam.ScreenTitle]: screenTitle || document.title,
      [AnalyticsEventParam.ScreenType]: screenType || getScreenType(),
      [AnalyticsEventParam.Title]: item?.title,
      [AnalyticsEventParam.TvShowTitle]: item?.tvShow?.title,
      [AnalyticsEventParam.Subtype]: item?.subtype,
      [AnalyticsEventParam.Extras]: item?.extras,
    })
  );
};

export const getBoundData = (
  value: string,
  context: AnalyticsContext,
  pluginId: string
) => {
  return applyDatabinding({
    value,
    context: transformContext(context, pluginId),
  });
};

export const databindObject = (
  object: Dictionary<string, string>,
  context: AnalyticsContext,
  pluginId: string
) => {
  const databoundObject = {} as Dictionary<string, string>;

  Object.entries(object).forEach(([key, value]) => {
    const result = getBoundData(value, context, pluginId);
    databoundObject[key] = result;
  });

  return databoundObject;
};

export const getScreenType = () => {
  const mappingKey = Object.keys(screenTypeMapping).find((key) =>
    window.location.pathname.includes(key)
  );

  return mappingKey ? screenTypeMapping[mappingKey] : Browse;
};

const bindingEntries = Object.entries(analyticsBindingMapping);

// TODO: this is inefficient, we shouldn't do this.
// we need to refactor context and/or binding mapping to skip this step.
const transformContext = (context: AnalyticsContext, pluginId: string) => {
  return Object.entries(context).reduce((result, [key, value]) => {
    if (!key.startsWith('tech')) {
      const entry = bindingEntries.find(([, param]) => param === key);

      if (entry) {
        set(result, entry[0], value);
      } else if (value) {
        // all `AnalyticsEventParam`s are added to the context with null value by default.
        // only warn about them if we try to pass an actual value.
        console.warn(
          `${pluginId} - No binding mapping was found for parameter "${key}".`
        );
      }
    }

    return result;
  }, {});
};

export const normalizeAnalyticsEvent = <T extends { [key: string]: any }>(
  object: T
): T => {
  // Shallow copying object should be enough
  const _object = { ...object };

  for (const key in _object) {
    _object[key] = (
      _object[key] === undefined ? null : _object[key]
    ) as T[Extract<keyof T, string>];
  }

  return _object;
};

// merge analytics configs coming from S3 features and Core integrations
export const mergeAnalyticsFeatureWithIntegrations = (
  config: MainConfig
): FeatureArrayData<FeatureAnalyticsProps> => {
  // getting event params from Core analyticsEvents
  const analyticsEventsByVendor = Object.keys(config.analyticsEvents).reduce(
    (acc, eventName) => {
      const vendors = Object.keys(config.analyticsEvents[eventName]);
      vendors.forEach((vendor) => {
        if (!acc[vendor]) {
          acc[vendor] = {};
        }
        acc[vendor][eventName] = config.analyticsEvents[eventName][vendor]?.map(
          (event) => ({
            ...event,
            coreEventName: eventName,
            ...config.integrations[vendor]?.configuration,
          })
        );
      });

      return acc;
    },
    {}
  );

  // getting analytics integrations from Core integrations
  const analyticsPluginIntegrationsMap = Object.keys(config.integrations)
    .filter((integration) =>
      config.integrations[integration]?.activeForFeatures?.includes(
        IntegrationsFeatures.ANALYTICS
      )
    )
    .map((integration) => {
      const plugin = config.pluginIntegrations?.[integration] || {};

      return {
        key: integration,
        plugin: {
          ...plugin,
          // rename `source` to `src` to match the expected property name
          src: plugin.source,
        },
        enabled: true,
        properties: {
          ...config.integrations[integration],
          structuredEvents: analyticsEventsByVendor[integration],
        },
      };
    }, []);

  return [
    ...(config?.features?.analytics || []),
    ...analyticsPluginIntegrationsMap,
  ];
};

// create payloads for structured events
export const getPayloadsForStructuredEvents = ({
  analyticsProps,
  event,
  analyticsEvent,
  pluginId,
  analyticsContext,
}) => {
  const eventsToSend = analyticsProps?.structuredEvents?.[event] || [];

  return eventsToSend.map((evt) => ({
    ...createAnalyticsPayload({
      config: { ...analyticsProps, properties: evt?.properties },
      event: analyticsEvent, // internal name, ex: screen.view
      context: analyticsContext,
      pluginId: pluginId || 'Unknown analytics plugin',
      databinder: getBoundData,
      useTypedBindings: true,
    }),
    eventName: evt.event, // outgoing name, ex: 'screenView', 'page_view', etc
  }));
};

// create payloads for unstructured events
export const getPayloadsForUnstructuredEvents = ({
  analyticsProps,
  analyticsEvent,
  populatedContext,
  pluginId,
}) => {
  const customPayload = createAnalyticsPayload({
    config: analyticsProps,
    event: analyticsEvent,
    context: populatedContext,
    pluginId,
    databinder: getBoundData,
  });

  return [customPayload];
};
